// Audio device preferences + media-error classification, shared by the
// softphone runtime and the Audio device manager modal.
//
// Preferences are per-machine (localStorage), applied as `ideal` constraints:
// a saved device that is unplugged silently falls back to the browser default
// instead of failing the call with OverconstrainedError. The only unfixable
// state is "no input device at all" — that is surfaced as a sticky device
// error by the softphone, never as a mid-call surprise.

const INPUT_DEVICE_KEY = 'sales-suite:audio-input-device';
const OUTPUT_DEVICE_KEY = 'sales-suite:audio-output-device';

function readKey(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(key, value);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // best-effort — a full/blocked storage just means no persistence
  }
}

export function readPreferredInputDeviceId(): string | null {
  return readKey(INPUT_DEVICE_KEY);
}

export function writePreferredInputDeviceId(deviceId: string | null): void {
  writeKey(INPUT_DEVICE_KEY, deviceId);
}

export function readPreferredOutputDeviceId(): string | null {
  return readKey(OUTPUT_DEVICE_KEY);
}

export function writePreferredOutputDeviceId(deviceId: string | null): void {
  writeKey(OUTPUT_DEVICE_KEY, deviceId);
}

// getUserMedia audio constraints honoring the saved mic preference. `ideal`
// (not `exact`) so calls keep working on the default mic when the preferred
// one is missing.
export function resolveAudioConstraints(
  preferredInputId: string | null = readPreferredInputDeviceId()
): boolean | MediaTrackConstraints {
  return preferredInputId ? { deviceId: { ideal: preferredInputId } } : true;
}

type MediaErrorKind = 'no-device' | 'denied' | 'busy' | 'other';

export interface ClassifiedMediaError {
  kind: MediaErrorKind;
  message: string;
}

const NO_DEVICE_NAMES = new Set(['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError']);
const DENIED_NAMES = new Set(['NotAllowedError', 'PermissionDeniedError', 'SecurityError']);
const BUSY_NAMES = new Set(['NotReadableError', 'TrackStartError', 'AbortError']);

export const NO_MICROPHONE_MESSAGE =
  'No microphone detected — plug in or reconnect your headset, then check the Audio button in the sidebar.';
export const MICROPHONE_DENIED_MESSAGE =
  'Microphone blocked — click the lock icon in your address bar, allow Microphone, then refresh the page.';
export const MICROPHONE_BUSY_MESSAGE =
  'Microphone is in use by another app — close other call apps (Zoom, Teams, …) and try again.';
export const ANSWER_LOOP_MESSAGE =
  'The phone failed to answer several calls in a row, so automatic dialing is paused. It retries on its own — check your headset via the Audio button in the sidebar if this persists.';

// Maps a getUserMedia/WebRTC failure to what the agent should do about it.
// The raw browser strings ("Requested device not found") are useless guidance
// on a headset-and-dialer workstation.
export function classifyMediaError(error: unknown): ClassifiedMediaError {
  const name = error instanceof DOMException || error instanceof Error ? error.name : '';
  const rawMessage = error instanceof Error ? error.message : '';
  if (NO_DEVICE_NAMES.has(name) || /requested device not found/i.test(rawMessage)) {
    return { kind: 'no-device', message: NO_MICROPHONE_MESSAGE };
  }
  if (DENIED_NAMES.has(name) || /permission denied|not allowed/i.test(rawMessage)) {
    return { kind: 'denied', message: MICROPHONE_DENIED_MESSAGE };
  }
  if (BUSY_NAMES.has(name)) {
    return { kind: 'busy', message: MICROPHONE_BUSY_MESSAGE };
  }
  return { kind: 'other', message: rawMessage || 'Microphone error' };
}

// Permission-granted does NOT imply device-present: Chrome keeps the mic
// permission "granted" with the headset unplugged. Callers must check this
// before treating the phone as able to answer.
// lib.dom types these as always present. Outside a secure context (and in SSR)
// they are undefined, and reaching through them throws. Returned from a
// function because a typed binding narrows straight back to the lib.dom type.
export function optionalMediaDevices(): MediaDevices | undefined {
  return typeof navigator === 'undefined' ? undefined : (navigator.mediaDevices as MediaDevices | undefined);
}

export function optionalPermissions(): Permissions | undefined {
  return typeof navigator === 'undefined' ? undefined : (navigator.permissions as Permissions | undefined);
}

export async function hasAudioInputDevice(): Promise<boolean> {
  const media = optionalMediaDevices();
  // No way to check — assume present rather than block the phone.
  if (!media) {
    return true;
  }
  try {
    const devices = await media.enumerateDevices();
    return devices.some((device) => device.kind === 'audioinput');
  } catch {
    return true;
  }
}

export function supportsOutputSelection(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

// Best-effort sink routing: missing support or a revoked device falls back to
// the system default output rather than erroring the call path.
export async function applyPreferredSink(element: HTMLMediaElement | null): Promise<void> {
  const outputId = readPreferredOutputDeviceId();
  if (!element || !outputId || !supportsOutputSelection()) {
    return;
  }
  try {
    await element.setSinkId(outputId);
  } catch {
    // device gone or sink change rejected — default output keeps working
  }
}
